import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { errorMessage } from '../lib/errors';
import { useServices } from '../services';

type Step = 'account' | 'verify';

const PASSWORD_RULE = 'At least 12 characters, with upper and lower case letters, a number and a symbol.';

function passwordProblem(pw: string): string | null {
  if (pw.length < 12) return PASSWORD_RULE;
  if (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw) || !/[0-9]/.test(pw) || !/[^A-Za-z0-9]/.test(pw)) return PASSWORD_RULE;
  return null;
}

/** Free-trial sign-up: create a login, confirm the emailed code, then set up the business. */
export function SignUpPage() {
  const { auth, mock } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('account');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [destination, setDestination] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      const msg = errorMessage(e);
      setError(/exists/i.test(msg) ? 'An account with this email already exists. Sign in instead, or reset your password.' : msg);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (step === 'account') {
      if (!name.trim()) return setError('Please enter your name.');
      if (!email.includes('@')) return setError('Please enter a valid email address.');
      const problem = passwordProblem(password);
      if (problem) return setError(problem);
      run(async () => {
        const r = await auth.signUp(email.trim().toLowerCase(), password, name.trim());
        setDestination(r.destination ?? email);
        setStep('verify');
      });
    } else {
      run(async () => {
        const result = await auth.confirmSignUp(email.trim().toLowerCase(), code.trim(), password);
        if (result.step !== 'done') throw new Error('Please sign in to continue.');
        qc.clear();
        qc.setQueryData(['auth'], true);
        try { sessionStorage.setItem('dp-owner-name', name.trim()); } catch { /* private mode */ }
        navigate('/', { replace: true });
      });
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <div className="wordmark" aria-label="DemandPilot">Demand<span>Pilot</span></div>
        <h1 style={{ fontSize: 24, marginBottom: 6 }}>{step === 'account' ? 'Start your free trial' : 'Check your email'}</h1>
        <p className="muted small" style={{ marginBottom: 18 }}>
          {step === 'account'
            ? '30 days free on your own sales data. No credit card.'
            : `We sent a 6-digit code to ${destination}. It can take a minute to arrive; check spam too.`}
        </p>
        {notice && <p className="form-success" role="status">{notice}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}

        {step === 'account' && (
          <>
            <div className="field">
              <label htmlFor="su-name">Your name</label>
              <input id="su-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </div>
            <div className="field">
              <label htmlFor="su-email">Work email</label>
              <input id="su-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="su-password">Password</label>
              <input id="su-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <span className="hint">{PASSWORD_RULE}</span>
            </div>
          </>
        )}
        {step === 'verify' && (
          <div className="field">
            <label htmlFor="su-code">Verification code</label>
            <input id="su-code" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          </div>
        )}

        <button type="submit" className="btn btn-primary" style={{ width: '100%', minHeight: 42 }} disabled={busy}>
          {busy ? 'Please wait…' : step === 'account' ? 'Create account' : 'Verify and continue'}
        </button>

        <div style={{ marginTop: 14, fontSize: 14, display: 'grid', gap: 8 }}>
          {step === 'verify' && (
            <button type="button" className="link-btn" disabled={busy}
              onClick={() => run(async () => { await auth.resendSignUpCode(email.trim().toLowerCase()); setNotice('We sent a new code.'); })}>
              Send a new code
            </button>
          )}
          <span className="muted">Already have an account? <Link to="/sign-in">Sign in</Link></span>
        </div>
        {mock && <p className="muted small" style={{ marginTop: 16, marginBottom: 0 }}>Mock mode: the verification code is <code>123456</code>.</p>}
      </form>
    </div>
  );
}
